import React, { FC, useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Tooltip from '@material-ui/core/Tooltip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LockIcon from '@material-ui/icons/Lock';
import PhoneIcon from '@material-ui/icons/Phone';
import SearchIcon from '@material-ui/icons/Search';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import clsx from 'clsx';
import { switchChildAccount } from 'clients/ChainClient';
import { isValid } from 'clients/Clients';
import { getSessionTokenClient } from 'utils/SessionUtils';
import {
  ChainResponseData,
  ChainSessionState,
  ChildStoreItem,
  fetchChainChildrenWithRetry,
  getValidatedChainSessionState,
  isChildStoreActive,
  restoreActiveChildSession,
  restoreParentSession,
  setChildImpersonationSession,
} from 'utils/ChainSessionUtils';
import { getLinkCDNImage } from 'utils/ImageUtils';
import NotifyUtils from 'utils/NotifyUtils';
import styles from './ImpersonationBanner.module.css';

const StoreAvatar: FC<{ name?: string; avatarURL?: string; fallbackLetter?: string }> = ({
  name,
  avatarURL,
  fallbackLetter,
}) => {
  const [hasError, setHasError] = useState(false);
  const cdnUrl = avatarURL && !hasError ? getLinkCDNImage(avatarURL) : '';

  if (cdnUrl) {
    return (
      <img
        src={cdnUrl}
        alt={name || 'Avatar'}
        className={styles.avatarImg}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div className={styles.avatarFallback}>
      {fallbackLetter || (name ? name.charAt(0).toUpperCase() : 'S')}
    </div>
  );
};

interface ImpersonationBannerProps {
  user?: any;
}

export const ImpersonationBanner: FC<ImpersonationBannerProps> = ({ user }) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const [chainSession, setChainSession] = useState<ChainSessionState | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [switchingAccountId, setSwitchingAccountId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [chainData, setChainData] = useState<ChainResponseData | null>(null);

  useEffect(() => {
    setMounted(true);
    setChainSession(getValidatedChainSessionState(user));
  }, [user]);

  useEffect(() => {
    if (!user || !user?.accountID || !chainSession?.isImpersonating) return;
    let isMounted = true;

    const loadChildren = async () => {
      setLoading(true);
      const loadedData = await fetchChainChildrenWithRetry(() => !isMounted);
      if (!isMounted) return;
      setLoading(false);

      if (loadedData) {
        setChainData(loadedData);
      } else {
        setChainData(null);
        NotifyUtils.error('Không thể tải danh sách chi nhánh. Vui lòng thử lại sau.');
      }
    };

    loadChildren();

    return () => {
      isMounted = false;
    };
  }, [user, chainSession?.isImpersonating]);

  if (!mounted || !user || !user?.accountID || !chainSession || !chainSession.isImpersonating) {
    return null;
  }

  const handleReturn = () => {
    if (switchingAccountId !== null) {
      return;
    }
    restoreParentSession();
    NotifyUtils.success('Đã trở về tài khoản chính');
    window.location.reload();
  };

  const activeChildAccountId = chainSession.activeChildAccountId;
  const activeChild = chainData?.children?.find((c) => c.accountID === activeChildAccountId);
  const childName = activeChild?.name || chainSession.activeChildStoreName || user?.name || 'Chi nhánh';
  const parentName = chainData?.parentName || chainSession.parentCustomerName || 'Cửa hàng chính';
  const parentAvatarURL = chainData?.parentAvatarURL;
  const totalStores = chainData?.totalStores || (chainData?.children ? chainData.children.length + 1 : 1);

  const currentPhone = activeChild?.phone || chainSession.activeChildPhone || user?.phone;
  const currentAddress = activeChild?.address || chainSession.activeChildAddress || user?.address;
  const hasValidAddress = Boolean(currentAddress);

  const filteredChildren = (chainData?.children || []).filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.code && c.code.toLowerCase().includes(q))
    );
  });

  const handleSwitchToChild = async (child: ChildStoreItem) => {
    if (switchingAccountId !== null || activeChildAccountId === child.accountID) {
      return;
    }
    setSwitchingAccountId(child.accountID);
    const activeSessionToken = getSessionTokenClient();
    const parentToken = chainSession.parentToken || activeSessionToken;

    try {
      const resp = await switchChildAccount({
        childAccountId: child.accountID,
        token: parentToken,
      });
      const impersonateToken = resp?.data?.[0]?.accessToken || resp?.data?.[0]?.access_token || resp?.data?.[0]?.token;
      if (isValid(resp) && impersonateToken) {
        setChildImpersonationSession(
          impersonateToken,
          parentToken,
          {
            accountID: chainData?.parentAccountID || chainSession.parentAccountId,
            name: chainData?.parentName || chainSession.parentCustomerName,
            code: chainSession.parentCustomerCode || '',
            chainCode: chainData?.chainCode || chainSession.chainCode || '',
          },
          {
            accountID: child.accountID,
            code: child.code,
            name: child.name,
            avatarURL: child.avatarURL,
            phone: child.phone,
            address: child.address,
            chainCode: chainData?.chainCode || chainSession.chainCode || '',
          }
        );
        NotifyUtils.success(`Đã chuyển sang chi nhánh: ${child.name}`);
        setDropdownOpen(false);
        window.location.reload();
      } else {
        if (activeSessionToken) {
          restoreActiveChildSession(activeSessionToken);
        }
        NotifyUtils.error(resp?.message || 'Không thể chuyển sang tài khoản chi nhánh này.');
        setSwitchingAccountId(null);
      }
    } catch (e: any) {
      if (activeSessionToken) {
        restoreActiveChildSession(activeSessionToken);
      }
      NotifyUtils.error(e?.message || 'Đã có lỗi xảy ra khi chuyển tài khoản.');
      setSwitchingAccountId(null);
    }
  };



  const renderTooltipContent = (name: string, phone?: string, address?: string, isActive = true) => (
    <div className={styles.tooltipContainer}>
      <div className={styles.tooltipName}>{name}</div>
      {phone ? <div className={styles.tooltipMeta}>SĐT: {phone}</div> : null}
      {address ? <div className={styles.tooltipMeta}>Địa chỉ: {address}</div> : null}
      {!isActive && (
        <div className={styles.tooltipInactiveWarning}>
          ⚠️ Tài khoản chi nhánh hiện chưa kích hoạt hoặc tạm khóa. Vui lòng liên hệ CSKH để được hỗ trợ.
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.bannerContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.leftGroup}>
          <div className={styles.roleTag}>
            <SupervisorAccountIcon className={styles.roleIcon} />
            <span>ĐẠI DIỆN CHUỖI</span>
          </div>
          <span className={styles.divider}>|</span>
          <span className={styles.bannerText}>
            Đang thao tác tại:{' '}
            <Tooltip title={childName} arrow placement="bottom">
              <span className={styles.bannerStoreName}>{childName}</span>
            </Tooltip>
          </span>
          {currentPhone ? (
            <>
              <span className={styles.bulletDot}>•</span>
              <Tooltip title={`SĐT: ${currentPhone}`} arrow placement="bottom">
                <span className={styles.metaItem}>
                  <PhoneIcon className={styles.metaIcon} />
                  <span className={styles.metaText}>{currentPhone}</span>
                </span>
              </Tooltip>
            </>
          ) : null}
          {hasValidAddress ? (
            <>
              <span className={styles.bulletDot}>•</span>
              <Tooltip title={`Địa chỉ: ${currentAddress}`} arrow placement="bottom">
                <span className={styles.metaItem}>
                  <LocationOnIcon className={styles.metaIcon} />
                  <span className={styles.metaTextAddress}>{currentAddress}</span>
                </span>
              </Tooltip>
            </>
          ) : null}
        </div>

        <div className={styles.rightGroup}>
          <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <div className={styles.dropdownContainer}>
              <button
                type="button"
                className={clsx(styles.dropdownTriggerBtn, dropdownOpen && styles.dropdownTriggerActive)}
                onClick={() => setDropdownOpen((v) => !v)}
              >
                <SwapHorizIcon fontSize="small" />
                <span>Đổi chi nhánh</span>
                <ExpandMoreIcon className={clsx(styles.chevronIcon, dropdownOpen && styles.chevronOpen)} />
              </button>

              {dropdownOpen && (
                <div className={styles.bannerDropdownPopover}>
                  <div className={styles.popoverTitle}>
                    <span>Hệ Thống Chi Nhánh</span>
                    <span className={styles.popoverCountBadge}>{totalStores} chi nhánh</span>
                  </div>

                  {totalStores > 3 && (
                    <div className={styles.searchBox}>
                      <SearchIcon className={styles.searchIcon} />
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Tìm kiếm chi nhánh..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  )}

                  <Tooltip
                    title={renderTooltipContent(parentName)}
                    arrow
                    placement="left"
                  >
                    <div
                      className={clsx(styles.parentCard, switchingAccountId !== null && styles.parentCardDisabled)}
                      onClick={handleReturn}
                    >
                      <div className={styles.storeMeta}>
                        <div className={styles.avatarWrapper}>
                          <StoreAvatar name={parentName} avatarURL={parentAvatarURL} fallbackLetter="M" />
                        </div>
                        <div className={styles.storeText}>
                          <span className={styles.storeName}>{parentName}</span>
                          <span className={styles.parentBadge}>CỬA HÀNG CHÍNH</span>
                        </div>
                      </div>
                      <Tooltip title="Quay lại tài khoản chính" arrow placement="top">
                        <div className={styles.switchIconBtn}>
                          <SwapHorizIcon fontSize="small" />
                        </div>
                      </Tooltip>
                    </div>
                  </Tooltip>

                  <div className={styles.sectionDivider}>
                    <span className={styles.sectionDividerText}>Chi nhánh trực thuộc</span>
                    <div className={styles.dividerLine} />
                  </div>

                  {loading ? (
                    <div className={styles.loadingNotice}>
                      <CircularProgress size={16} className={styles.switchingSpinner} />
                      <span>Đang tải danh sách chi nhánh...</span>
                    </div>
                  ) : (
                    <div className={styles.childList}>
                      {filteredChildren.length === 0 ? (
                        <div className={styles.emptyNotice}>Không tìm thấy chi nhánh phù hợp</div>
                      ) : (
                        filteredChildren.map((child) => {
                          const isStoreActive = isChildStoreActive(child);
                          const isCurrentActive = activeChildAccountId === child.accountID;
                          const isSwitchingThis = switchingAccountId === child.accountID;

                          return (
                            <Tooltip
                              key={child.customerID}
                              title={renderTooltipContent(child.name, child.phone, child.address, isStoreActive)}
                              arrow
                              placement="left"
                            >
                              <div
                                className={clsx(
                                  styles.childItem,
                                  isCurrentActive && styles.childItemActive,
                                  (!isStoreActive || (switchingAccountId !== null && !isSwitchingThis)) && styles.childItemDisabled,
                                  switchingAccountId !== null && !isSwitchingThis && styles.itemDisabledDuringSwitch
                                )}
                                onClick={() => {
                                  if (!isStoreActive || switchingAccountId !== null) return;
                                  if (!isCurrentActive) {
                                    handleSwitchToChild(child);
                                  }
                                }}
                              >
                                <div className={styles.storeMeta}>
                                  <div className={styles.avatarWrapper}>
                                    <StoreAvatar name={child.name} avatarURL={child.avatarURL} fallbackLetter="C" />
                                  </div>
                                  <div className={styles.storeText}>
                                    <span className={styles.storeName}>{child.name}</span>
                                    {!isStoreActive && (
                                      <span className={styles.inactiveStoreBadge}>Chưa kích hoạt</span>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  {!isStoreActive ? (
                                    <Tooltip title="Tài khoản chưa kích hoạt - Liên hệ CSKH" arrow placement="top">
                                      <div className={styles.disabledLockBtn}>
                                        <LockIcon fontSize="small" />
                                      </div>
                                    </Tooltip>
                                  ) : isSwitchingThis ? (
                                    <CircularProgress size={16} className={styles.switchingSpinner} />
                                  ) : isCurrentActive ? (
                                    <CheckCircleIcon className={styles.activeCheckIcon} />
                                  ) : (
                                    <Tooltip title="Chuyển sang chi nhánh này" arrow placement="top">
                                      <div className={styles.switchIconBtn}>
                                        <SwapHorizIcon fontSize="small" />
                                      </div>
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            </Tooltip>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </ClickAwayListener>

          <button
            type="button"
            className={clsx(styles.returnBtn, switchingAccountId !== null && styles.itemDisabledDuringSwitch)}
            onClick={handleReturn}
          >
            <ExitToAppIcon fontSize="small" />
            <span>Trở về tài khoản chính</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
